/* =============================================================
   useTaskCompletions.js — Student task completion state
   -------------------------------------------------------------
   Fetches and toggles checkbox-type task completions for a
   single student from the student_task_completions table.

   Usage:
     const { completions, loading, toggleTask }
       = useTaskCompletions(studentId);

   Returns:
     completions — Set<process_step_document_id> of completed tasks
     loading     — true on initial fetch
     toggleTask  — (psdId) => Promise  check / uncheck a task
   ============================================================= */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export function useTaskCompletions(studentId) {
  const [completions, setCompletions] = useState(new Set());
  const [loading,     setLoading]     = useState(true);

  const fetchCompletions = useCallback(async () => {
    if (!studentId) { setLoading(false); return; }
    const { data } = await supabase
      .from('student_task_completions')
      .select('process_step_document_id')
      .eq('student_id', studentId);
    setCompletions(new Set((data ?? []).map(r => r.process_step_document_id)));
    setLoading(false);
  }, [studentId]);

  useEffect(() => { fetchCompletions(); }, [fetchCompletions]);

  const toggleTask = useCallback(async (psdId) => {
    if (!studentId) return;
    const isDone = completions.has(psdId);

    // Optimistic update
    setCompletions(prev => {
      const next = new Set(prev);
      if (isDone) next.delete(psdId);
      else        next.add(psdId);
      return next;
    });

    if (isDone) {
      const { error } = await supabase
        .from('student_task_completions')
        .delete()
        .eq('student_id', studentId)
        .eq('process_step_document_id', psdId);
      if (error) {
        // Revert optimistic update
        setCompletions(prev => new Set([...prev, psdId]));
        console.error('[useTaskCompletions] delete failed:', error.message);
      }
    } else {
      const { error } = await supabase
        .from('student_task_completions')
        .insert({ student_id: studentId, process_step_document_id: psdId });
      if (error) {
        // Revert optimistic update
        setCompletions(prev => {
          const next = new Set(prev);
          next.delete(psdId);
          return next;
        });
        console.error('[useTaskCompletions] insert failed:', error.message);
      }
    }
  }, [studentId, completions]);

  return { completions, loading, toggleTask };
}
